<template>

<Drawer :title="panelTitle" initialWidth="xl" :showDefaultCloseButton="false" @requestUnmount="close">

	<template #default="{ hide }">

		<form class="grid grid-cols-1 gap-6 pt-8">

			<div class="pb-20 px-6">

				{{ $t('Select the pupils who can participate in this activity') }}

				<div class="mt-2">

					<div class="border-b mb-2 pb-2 flex gap-3 items-center">
						<input type="checkbox"
									id="select-all"
									class="checkbox checkbox-sm"
									@change.prevent="toggleSelectAll"
									ref="checkAll"
						/>
						<label for="select-all">{{ $t('Select all') }}</label>
					</div>

					<ul>
						<li v-for="pupil in pupilsStore.items" :key="pupil.id" class="mb-2 flex gap-3 items-center">
							<input
									type="checkbox"
									:id="'pupil-' + pupil.id"
									class="checkbox checkbox-sm checkbox-primary"
									:checked="selectedItem.isPupilSelected(pupil)"
									@change.prevent="event => this.togglePupil(pupil, event.target.checked)"
							/>
							<label :for="'pupil-' + pupil.id">{{ pupil.getFullName() }}</label>
						</li>
					</ul>
				</div>

			</div>

			<div class="flex justify-end gap-1 absolute bottom-0 w-full bg-white/50 backdrop-blur-sm z-10 p-4 mt-6 border-t border-base-200">
				<button type="button" class="btn btn-ghost" @click="hide">
					{{ $t('Cancel') }}
				</button>
				<button type="button" class="btn btn-primary" @click="applyChanges(hide)">
					{{ $t('Apply') }}
				</button>
			</div>
		</form>

	</template>
</Drawer>
</template>

<script lang="ts">
import {mapStores} from "pinia";
import {usePupilsStore} from "@/stores/Pupils.store";
import {LessonItem} from "@/models/LessonItem.model";
import {Pupil} from "@/models/Pupil.model";
import {useClassroomsStore} from "@/stores/Classrooms.store";

import IconSelector from "@/components/IconSelector.vue";
import ActivitySymbol from "@/components/ui/symbols/ActivitySymbol.vue";
import NumberInput from '@/components/ui/NumberInput.vue';
import { PencilIcon } from "@heroicons/vue/24/outline";
import Drawer from '@/components/ui/Drawer.v2.vue';
import { Icon } from "@/models/Icon.model";

export default {
	emits: [
		'requestClose',
		'update:modelValue',
	],

	props: {
		lessonItem: {
			type: LessonItem,	// LessonItem
			required: true,
		},
	},

	components: {
		IconSelector,
		ActivitySymbol,
		NumberInput,
		PencilIcon,
		Drawer,
	},

	data() {
		return {
			allSelected: false,
			someSelected: false,
			selectedItem: null
		}
	},

	computed: {
		...mapStores(usePupilsStore, useClassroomsStore),

		panelTitle() {
			let title = this.$t('Settings');
			if( this.lessonItem.activity) {
				title += ' - ' + this.lessonItem.activity.name;
			}
			return title;
		},
	},

	async mounted() {
		await this.pupilsStore.loadAllForClassroom();
		this.updateAllSelected();
	},

	created	() {
		this.selectedItem = this.lessonItem.clone();
	},

	methods: {
		close() {
			this.$emit('requestClose');
		},

		applyChanges(closeDrawerCallback) {
			// ... update pupils attribute value
			this.$emit('update:modelValue', this.selectedItem);
			closeDrawerCallback();
		},

		toggleSelectAll(event) {
			if (this.selectedItem.allowedPupilIds.length > 0) {
				this.selectedItem.allowedPupilIds = [];
			} else {
				this.selectedItem.allowedPupilIds = this.pupilsStore.items.map(pupil => pupil.id);
			}

			this.updateAllSelected();
		},

		togglePupil(pupil: Pupil, selected: boolean) {
			this.selectedItem.setPupilSelected(pupil, selected);
			this.updateAllSelected();
		},

		updateAllSelected() {
			this.allSelected = this.selectedItem.allowedPupilIds.length >= this.pupilsStore.items.length;
			this.someSelected = this.selectedItem.allowedPupilIds.length > 0 &&
					this.selectedItem.allowedPupilIds.length < this.pupilsStore.items.length;

			this.$nextTick(() => {
				this.$refs.checkAll.indeterminate = this.someSelected;
				this.$refs.checkAll.checked = this.selectedItem.allowedPupilIds.length > 0;
			});
		},

		setIcon(eventData: Icon) {
			const icon = eventData;
			this.selectedItem.icon = icon;
		},
	}
}
</script>
